import jwtAuthAxios from "./auth/jwtAuth";
import { serializeCompanySettingValue } from "app/utils/companySettings";

const companyServices = {};
const OMITTED_COMPANY_SETTINGS_FIELDS = ['id', 'created_at', 'updated_at', 'logo_url', 'available_dropoff_carriers'];

// includeInactive solo lo respeta el backend para superadmin. Sin el, las companias
// dadas de baja no vienen: hacen falta para poder reactivarlas.
companyServices.getAllCompanies = async (includeInactive = false) => {
    const {data} = await jwtAuthAxios.get('/companies', {
        params: includeInactive ? { include_inactive: true } : {},
    });
    return data;
};

companyServices.deactivateCompany = async (id) => {
    const {data} = await jwtAuthAxios.post(`/companies/${id}/deactivate`);
    return data;
};

companyServices.activateCompany = async (id) => {
    const {data} = await jwtAuthAxios.post(`/companies/${id}/activate`);
    return data;
};

// Que se llevaria por delante el borrado definitivo. Se pide antes de mostrar el
// modal: la cuenta es lo unico que le dice a la persona el tamano de lo que va a
// borrar, y despues de confirmar ya no hay vuelta.
companyServices.getCompanyDeletionPlan = async (id) => {
    const { data } = await jwtAuthAxios.get(`/companies/${id}/deletion_plan`);
    return data;
};

// Borrado definitivo. `confirmation` es la palabra que la persona escribio: el
// backend la exige y no borra nada sin ella.
companyServices.deleteCompany = async (id, confirmation) => {
    const { data } = await jwtAuthAxios.delete(`/companies/${id}`, { data: { confirmation } });
    return data;
};

companyServices.getCompanySettings = async (id) => {
    const {data} = await jwtAuthAxios.get(`/companies/${id}/settings`);
    return data;
};

companyServices.saveCompanySettings = async (id, params) => {
    var formData = new FormData();

    Object.keys(params).forEach((k) => {
        if(OMITTED_COMPANY_SETTINGS_FIELDS.includes(k)) {
        } else if(k === 'new_logo' && !params[k]) {
        } else if(params[k] !== null && params[k] !== undefined) {
            formData.append(k, serializeCompanySettingValue(params[k]));
        } else {
            formData.append(k, '');
        }
    })
    
    const {data} = await jwtAuthAxios.post(`/companies/${id}/settings`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return data;
};

companyServices.getCompanyPlugins = async (id) => {
    const {data} = await jwtAuthAxios.get(`/companies/${id}/plugins`);
    return data;
};

companyServices.createDropoffCarrier = async (id, { name, icon }) => {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('icon', icon);

    const { data } = await jwtAuthAxios.post(`/companies/${id}/dropoff_carriers`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });

    return data;
};

// carrierId es el id de una compañía dropoff personalizada o la key de un carrier
// del catálogo base, que no tiene fila hasta que se le configura una dirección.
companyServices.updateDropoffCarrier = async (id, carrierId, { name, icon, address }) => {
    const formData = new FormData();
    if (name !== undefined && name !== null) {
        formData.append('name', name);
    }
    if (icon) {
        formData.append('icon', icon);
    }
    // Se manda siempre que venga definida, incluso vacía: así se puede borrar
    // la dirección por defecto dejando el campo en blanco.
    if (address !== undefined && address !== null) {
        formData.append('address', address);
    }

    const { data } = await jwtAuthAxios.patch(
        `/companies/${id}/dropoff_carriers/${carrierId}`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }
    );

    return data;
};

companyServices.deleteDropoffCarrier = async (id, carrierId) => {
    const { data } = await jwtAuthAxios.delete(`/companies/${id}/dropoff_carriers/${carrierId}`);
    return data;
};

companyServices.getRetailCustomers = async (id) => {
    const { data } = await jwtAuthAxios.get(`/companies/${id}/retail_customers`);
    return data;
};

companyServices.searchRetailCandidates = async (id, q) => {
    const { data } = await jwtAuthAxios.get(`/companies/${id}/search_retail_candidates`, { params: { q } });
    return data;
};

companyServices.addRetailCustomer = async (id, customerId) => {
    const { data } = await jwtAuthAxios.post(`/companies/${id}/retail_customers`, { customer_id: customerId });
    return data;
};

companyServices.removeRetailCustomer = async (id, customerId) => {
    const { data } = await jwtAuthAxios.delete(`/companies/${id}/retail_customers/${customerId}`);
    return data;
};

// Carga inicial de un cliente nuevo. Va por /onboarding y no por /companies
// porque crear una compania no es una operacion sobre una compania existente.
companyServices.downloadOnboardingTemplate = async () => {
    const { data } = await jwtAuthAxios.get(`/onboarding/template`, {
        responseType: "arraybuffer",
    });
    return data;
};

companyServices.importOnboarding = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const { data } = await jwtAuthAxios.post(`/onboarding/import`, formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });

    return data;
};

// Alta a mano, sin archivo: la compania y sus usuarios se escriben en el modal.
// Del otro lado entra por el mismo importer que el Excel, asi que devuelve lo
// mismo: los conteos y las claves temporales que se muestran una sola vez.
companyServices.createOnboardingCompany = async ({ company, centres, users }) => {
    const { data } = await jwtAuthAxios.post(`/onboarding/companies`, { company, centres, users });
    return data;
};

export default companyServices;
